import { useMemo, useRef, useState } from 'react';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import EditorNumber, {
  type EditorNumberProps
} from '~/components/editor/EditorNumber/EditorNumber';
import EditorSlider from '~/components/editor/EditorSlider/EditorSlider';
import { useExperiment } from '~/systems/experiment';
import {
  assertNever,
  collisionWithinSet,
  generateCircularPattern,
  generateGridPattern,
  generateTriangularPattern,
  maxDistanceFromOriginInSet,
  smoothmin,
  Transform,
  useDOMNodeSize,
  Vec2
} from '~/util';
import type { Size } from '~/util';
import './PatternGenerator.css';

interface PatternGeneratorProps {
  maxPoints: number;
  minSep: number;
  maxDist: number;
  rootSize: Size;
  onGenerate: (pattern: Vec2[]) => void;
}

type PatternKind = 'grid' | 'circle' | 'triangle';

/** Height of the sample viewbox */
const HEIGHT = 30;
/** Width of the sample viewbox */
const WIDTH = 30;
/** Radius of the sample atoms */
const RADIUS = 2;
/** Separation of the sample atoms */
const SEPARATION = 4;

const SAMPLE_TRANSFORM: Record<PatternKind, Transform> = {
  grid: [2, 0, 0, -2, WIDTH / 2, HEIGHT / 2],
  circle: [1.7, 0, 0, -1.7, WIDTH / 2, HEIGHT / 2],
  triangle: [2, 0, 0, -2, WIDTH / 2, (2 / 3) * HEIGHT - 1]
};

const SAMPLE_GRID_CONF = {
  kind: 'grid' as const,
  rows: 3,
  cols: 3,
  sepX: SEPARATION,
  sepY: SEPARATION
};
const SAMPLE_CIRCLE_CONF = {
  kind: 'circle' as const,
  numParticles: 8,
  sep: SEPARATION
};
const SAMPLE_TRIANGLE_CONF = {
  kind: 'triangle' as const,
  numBaseParticles: 3,
  sepX: SEPARATION,
  sepY: SEPARATION
};

const PATTERN_LIMIT = 20;

type PatternConf =
  | typeof SAMPLE_GRID_CONF
  | typeof SAMPLE_CIRCLE_CONF
  | typeof SAMPLE_TRIANGLE_CONF;

function generatePattern(pattern: PatternConf, transform?: Transform): Vec2[] {
  let points: Vec2[] = [];
  switch (pattern.kind) {
    case 'grid':
      points = generateGridPattern(
        pattern.rows,
        pattern.cols,
        pattern.sepX,
        pattern.sepY
      );
      break;
    case 'triangle':
      points = generateTriangularPattern(
        pattern.numBaseParticles,
        pattern.sepX,
        pattern.sepY
      );
      break;
    case 'circle':
      points = generateCircularPattern(pattern.numParticles, pattern.sep);
      break;
    default:
      assertNever(pattern);
  }
  return transform == null
    ? points
    : Transform.transformPoints(transform, points);
}

const samplePatterns = [
  SAMPLE_GRID_CONF,
  SAMPLE_CIRCLE_CONF,
  SAMPLE_TRIANGLE_CONF
] as const;

interface PatternFieldProps extends EditorNumberProps {
  showSlider: boolean;
}

function PatternField({ showSlider, ...props }: PatternFieldProps) {
  return showSlider ? <EditorSlider {...props} /> : <EditorNumber {...props} />;
}

export default function PatternGenerator({
  maxPoints,
  minSep,
  maxDist,
  rootSize,
  onGenerate
}: PatternGeneratorProps) {
  const { commitedAtoms } = useExperiment();
  const [selectedPattern, setSelectedPattern] = useState<PatternKind>('grid');
  const [validationErrorMsg, setValidationErrorMsg] = useState<string | null>(
    null
  );

  const [gridInput, setGridInput] = useState({
    rows: 2,
    cols: 2,
    sepX: minSep,
    sepY: minSep
  });
  const [circleInput, setCircleInput] = useState({
    sep: minSep,
    numParticles: 5
  });
  const [triangleInput, setTriangleInput] = useState({
    numBaseParticles: 3,
    sepX: minSep,
    sepY: minSep
  });

  const previewRef = useRef<HTMLDivElement>(null);
  const previewSize = useDOMNodeSize(previewRef);

  const showPreview = rootSize.height >= 225 && rootSize.width >= 400;
  const showSliders = rootSize.height >= 335;

  const samples = useMemo(
    () =>
      samplePatterns.map((p) => generatePattern(p, SAMPLE_TRANSFORM[p.kind])),
    []
  );

  const patternInputs = useMemo(
    () => ({
      grid: { ...gridInput, kind: 'grid' as const },
      circle: { ...circleInput, kind: 'circle' as const },
      triangle: { ...triangleInput, kind: 'triangle' as const }
    }),
    [gridInput, circleInput, triangleInput]
  );

  const patternPreview = useMemo(() => {
    const empty = { radius: 0, path: '', refPath: '' };
    if (!showPreview || previewSize.width <= 0 || previewSize.height <= 0) {
      return empty;
    }

    const baseRadius = 1;
    const kind = selectedPattern;
    const pattern: Vec2[] = generatePattern(patternInputs[kind] as PatternConf);

    const maxDisplacement = Vec2.zero();
    for (const point of pattern) {
      maxDisplacement.x = Math.max(maxDisplacement.x, Math.abs(point.x));
      maxDisplacement.y = Math.max(maxDisplacement.y, Math.abs(point.y));
    }

    const PADDING = 10;
    maxDisplacement.x += baseRadius + PADDING;
    maxDisplacement.y += baseRadius + PADDING;

    const previewTriangle = selectedPattern === 'triangle';
    const centerX = previewSize.width / 2;
    const centerY = previewTriangle
      ? (2 / 3) * previewSize.height
      : previewSize.height / 2;
    const scaleX = centerX / maxDisplacement.x;
    const scaleY = centerY / maxDisplacement.y;
    const unboundScale = Math.min(scaleX, scaleY);

    const scale = smoothmin(5, unboundScale, 2);
    if (!Number.isFinite(scale) || scale < 0.01) {
      return empty;
    }

    const smoothScaleX = scale;
    const smoothScaleY = previewTriangle ? -scale : scale;

    let path = '';
    for (const p of pattern) {
      path += `M ${p.x * smoothScaleX + centerX} ${p.y * smoothScaleY + centerY} l0 0`;
    }

    const radius = baseRadius * scale + 0.3;

    const REF_UNITS = 20;
    const maxSpan = 50;
    const refXSpan = Math.min(
      maxSpan,
      Math.max(0, Math.floor(centerX / scale / REF_UNITS))
    );
    const refYSpan = Math.min(
      maxSpan,
      Math.max(0, Math.floor(centerY / scale / REF_UNITS))
    );
    let refPath = '';
    for (let i = -refXSpan; i <= refXSpan; i += 1) {
      refPath += `M ${i * REF_UNITS * scale + centerX} 0 l0 ${centerY * 2}`;
    }
    for (let i = -refYSpan; i <= refYSpan; i += 1) {
      refPath += `M0 ${i * REF_UNITS * scale + centerY}l${centerX * 2} 0`;
    }

    return { radius, path, refPath };
  }, [selectedPattern, patternInputs, previewSize, showPreview]);

  function onGeneratePattern() {
    setValidationErrorMsg(null);
    const numExistingAtoms = commitedAtoms.length;
    const conf = patternInputs[selectedPattern] as PatternConf;
    const positions = generatePattern(conf);

    if (positions.length + numExistingAtoms > maxPoints) {
      setValidationErrorMsg(
        `Too many atoms: (${positions.length + numExistingAtoms}). Max is ${maxPoints}`
      );
      return;
    }
    const maxPosDistance = maxDistanceFromOriginInSet(positions);
    if (maxPosDistance.distance > maxDist) {
      setValidationErrorMsg(
        `Atom at (${maxPosDistance.position.x}, ${maxPosDistance.position.y}) is outside of register bounds (radius ${maxDist}).`
      );
      return;
    }
    const collidingVectors = collisionWithinSet(positions, minSep);
    if (collidingVectors !== undefined) {
      setValidationErrorMsg(
        `Atoms at (${collidingVectors[0].x}, ${collidingVectors[0].y}) and (${collidingVectors[1].x}, ${collidingVectors[1].y}) are colliding within the threshold of ${minSep.toFixed(2)}`
      );
      return;
    }
    onGenerate(positions);
  }

  return (
    <div className="PatternGenerator">
      <div className="PatternGenerator-header">
        {validationErrorMsg && (
          <span className="PatternGenerator-error">{validationErrorMsg}</span>
        )}
        <p>Choose pattern</p>
        <div className="PatternGenerator-samplesPicker">
          {samplePatterns.map((p, i) => (
            <svg
              key={i}
              height={HEIGHT}
              width={WIDTH}
              className={`PatternGenerator-sample${selectedPattern === p.kind ? ' is-active' : ''}`}
              onClick={() => setSelectedPattern(p.kind)}
            >
              {samples[i].map((pos, idx) => (
                <circle key={idx} cx={pos.x} cy={pos.y} r={RADIUS} />
              ))}
            </svg>
          ))}
        </div>
      </div>
      <div className="PatternGenerator-inner">
        {showPreview && (
          <div ref={previewRef} className="PatternGenerator-preview">
            <svg
              height={previewSize.height}
              width={previewSize.width}
              className="PatternGenerator-previewSvg"
            >
              <path
                d={patternPreview.refPath}
                className="PatternGenerator-previewRefPath"
                strokeWidth={1}
              />
              <path
                d={patternPreview.path}
                className="PatternGenerator-previewPath"
                strokeWidth={patternPreview.radius * 2}
              />
            </svg>
          </div>
        )}
        <div
          className={`PatternGenerator-options${!showPreview ? ' PatternGenerator-options--center' : ''}`}
        >
          {selectedPattern === 'grid' && (
            <>
              <PatternField
                showSlider={showSliders}
                label="Columns (X)"
                name="columns-x"
                value={gridInput.cols}
                onChange={(v) => setGridInput((g) => ({ ...g, cols: v }))}
                max={PATTERN_LIMIT}
                min={1}
                step={1}
              />
              <PatternField
                showSlider={showSliders}
                label="Rows (Y)"
                name="rows-y"
                value={gridInput.rows}
                onChange={(v) => setGridInput((g) => ({ ...g, rows: v }))}
                max={PATTERN_LIMIT}
                min={1}
                step={1}
              />
              <PatternField
                showSlider={showSliders}
                label="Separation X"
                name="separation-x"
                value={gridInput.sepX}
                onChange={(v) => setGridInput((g) => ({ ...g, sepX: v }))}
                max={maxDist}
                min={minSep}
                step={0.01}
                unit="μm"
              />
              <PatternField
                showSlider={showSliders}
                label="Separation Y"
                name="separation-y"
                value={gridInput.sepY}
                onChange={(v) => setGridInput((g) => ({ ...g, sepY: v }))}
                max={maxDist}
                min={minSep}
                step={0.01}
                unit="μm"
              />
            </>
          )}
          {selectedPattern === 'circle' && (
            <>
              <PatternField
                showSlider={showSliders}
                label="Atom count"
                name="atom-count"
                value={circleInput.numParticles}
                onChange={(v) =>
                  setCircleInput((c) => ({ ...c, numParticles: v }))
                }
                max={maxPoints}
                min={1}
                step={1}
              />
              <PatternField
                showSlider={showSliders}
                label="Separation between atoms"
                name="separation-between-atoms"
                value={circleInput.sep}
                onChange={(v) => setCircleInput((c) => ({ ...c, sep: v }))}
                max={maxDist}
                min={minSep}
                step={0.01}
                unit="μm"
              />
            </>
          )}
          {selectedPattern === 'triangle' && (
            <>
              <PatternField
                showSlider={showSliders}
                label="Row count"
                name="row-count"
                value={triangleInput.numBaseParticles}
                onChange={(v) =>
                  setTriangleInput((t) => ({ ...t, numBaseParticles: v }))
                }
                max={PATTERN_LIMIT}
                min={1}
                step={1}
              />
              <PatternField
                showSlider={showSliders}
                label="Distance X"
                name="distance-x"
                value={triangleInput.sepX}
                onChange={(v) => setTriangleInput((t) => ({ ...t, sepX: v }))}
                max={maxDist}
                min={minSep}
                step={0.01}
                unit="μm"
              />
              <PatternField
                showSlider={showSliders}
                label="Distance Y"
                name="distance-y"
                value={triangleInput.sepY}
                onChange={(v) => setTriangleInput((t) => ({ ...t, sepY: v }))}
                max={maxDist}
                min={minSep}
                step={0.01}
                unit="μm"
              />
            </>
          )}
          <div className="PatternGenerator-generateRow">
            <EditorButton kind="primary" onClick={onGeneratePattern}>
              Generate pattern
            </EditorButton>
          </div>
        </div>
      </div>
    </div>
  );
}
